import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoybuy.quest';

const articleJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Как использовать YOYBUY Spreadsheet: полное руководство для заказа товаров из Китая',
    description: 'Пошаговое руководство по использованию YOYBUY Spreadsheet — самого удобного инструмента для заказа товаров с Taobao, 1688 и других китайских платформ. Узнайте, как заполнять таблицу, отправлять заказы и отслеживать доставку.',
    image: 'https://readdy.ai/api/search-image?query=Overhead%20shot%20of%20hands%20typing%20on%20silver%20laptop%20with%20bright%20colorful%20spreadsheet%20visible%20on%20screen%20showing%20product%20links%20and%20order%20data%2C%20surrounded%20by%20international%20shipping%20labels%20customs%20forms%20and%20Chinese%20marketplace%20printouts%2C%20warm%20overhead%20desk%20lamp%20lighting%2C%20busy%20productive%20e-commerce%20workspace%2C%20authentic%20importer%20work%20environment%2C%20rich%20textural%20details&width=1600&height=800&seq=article-hero-guide-v2&orientation=landscape',
    datePublished: '2026-07-04',
    dateModified: '2026-07-04',
    author: { '@type': 'Organization', name: 'YOYBUY Spreadsheet' },
    publisher: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
      },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/article/yoybuy-spreadsheet-guide` },
    inLanguage: 'ru',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Главная', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Руководство YOYBUY Spreadsheet', item: `${siteUrl}/article/yoybuy-spreadsheet-guide` },
    ],
  },
];

export default function YoybuySpreadsheetGuide() {
  return (
    <>
      <JsonLd data={articleJsonLd[0]} />
      <JsonLd data={articleJsonLd[1]} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-14 md:pb-20">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap">
                <li><a href="/" className="hover:text-primary-500 transition-colors no-underline">Главная</a></li>
                <li><i className="ri-arrow-right-s-line text-xs w-3 h-3 flex items-center justify-center"></i></li>
                <li className="text-foreground-600 font-medium">Руководство YOYBUY Spreadsheet</li>
              </ol>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-wider uppercase text-primary-600 bg-primary-50 rounded-full">
                Руководство 2026
              </span>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Как использовать YOYBUY Spreadsheet: полное руководство для заказа товаров из Китая
              </h1>
              <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
                Пошаговое руководство по использованию YOYBUY Spreadsheet — самого удобного инструмента для заказа товаров с Taobao, 1688 и других китайских платформ. Узнайте, как заполнять таблицу, отправлять заказы и отслеживать доставку.
              </p>
            </header>

            <div className="relative rounded-2xl overflow-hidden h-[320px] md:h-[420px] mb-12">
              <img
                src="https://readdy.ai/api/search-image?query=Overhead%20shot%20of%20hands%20typing%20on%20silver%20laptop%20with%20bright%20colorful%20spreadsheet%20visible%20on%20screen%20showing%20product%20links%20and%20order%20data%2C%20surrounded%20by%20international%20shipping%20labels%20customs%20forms%20and%20Chinese%20marketplace%20printouts%2C%20warm%20overhead%20desk%20lamp%20lighting%2C%20busy%20productive%20e-commerce%20workspace%2C%20authentic%20importer%20work%20environment%2C%20rich%20textural%20details&width=1600&height=800&seq=article-hero-guide-v2&orientation=landscape"
                alt="YOYBUY Spreadsheet руководство"
                title="Как использовать YOYBUY Spreadsheet"
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Что такое YOYBUY Spreadsheet и зачем он нужен
                </h2>
                <p className="text-sm md:text-base leading-relaxed mb-4">
                  <strong>YOYBUY Spreadsheet</strong> — это инновационная система заказа товаров из Китая, которая полностью меняет подход к международному шопингу. Вместо того чтобы тратить часы на ручное оформление каждого заказа через традиционные сервисы-посредники, вы просто заполняете удобную электронную таблицу со списком нужных товаров — и система автоматически обрабатывает ваш заказ.
                </p>
                <p className="text-sm md:text-base leading-relaxed mb-4">
                  Представьте: вы нашли десятки интересных позиций на Taobao, 1688, Weidian и других китайских маркетплейсах. При обычном подходе вам пришлось бы открывать каждый товар в отдельной вкладке, копировать ссылки, заполнять формы с размерами и цветами, рассчитывать стоимость доставки — и всё это с риском допустить ошибку. С <strong>YOYBUY Spreadsheet</strong> весь этот процесс занимает считанные минуты.
                </p>
                <p className="text-sm md:text-base leading-relaxed">
                  Система <strong>YOYBUY Spreadsheet</strong> была разработана специально для тех, кто регулярно заказывает товары из Китая — владельцев интернет-магазинов, дропшипперов, оптовых покупателей и просто энтузиастов китайского шопинга. Табличный формат позволяет легко управлять десятками и сотнями позиций одновременно, отслеживать статус каждого заказа и всегда иметь полную картину расходов.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Преимущества YOYBUY Spreadsheet перед ручными заказами
                </h2>
                <ul className="space-y-3 text-sm md:text-base">
                  <li className="flex gap-3">
                    <span className="text-primary-500 font-bold mt-0.5 shrink-0">•</span>
                    <span><strong>Экономия времени:</strong> Один заказ через таблицу занимает 2-3 минуты вместо 15-20 минут при ручном оформлении. При массовых закупках разница составляет часы.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary-500 font-bold mt-0.5 shrink-0">•</span>
                    <span><strong>Минимум ошибок:</strong> Автоматическая проверка артикулов, размеров и цветов снижает вероятность ошибки практически до нуля.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary-500 font-bold mt-0.5 shrink-0">•</span>
                    <span><strong>Прозрачность расходов:</strong> Вы заранее видите полную стоимость заказа, включая все комиссии и доставку. Никаких неожиданных списаний.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary-500 font-bold mt-0.5 shrink-0">•</span>
                    <span><strong>Массовые закупки:</strong> Таблица оптимизирована для работы с большими объемами — сотни позиций обрабатываются так же легко, как и десять.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary-500 font-bold mt-0.5 shrink-0">•</span>
                    <span><strong>Удобное отслеживание:</strong> Все статусы заказов собраны в одном месте. Вы всегда знаете, на каком этапе находится каждая позиция.</span>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Пошаговая инструкция: как заказать через YOYBUY Spreadsheet
                </h2>

                <div className="space-y-6">
                  <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                    <h3 className="text-base font-bold text-foreground-800 mb-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-500 text-background-50 text-xs font-bold mr-2">1</span>
                      Найдите нужные товары на китайских платформах
                    </h3>
                    <p className="text-sm text-foreground-600 ml-9">
                      Откройте Taobao, 1688, Weidian или любой другой китайский маркетплейс и найдите товары, которые хотите заказать. Скопируйте ссылки на страницы товаров — они понадобятся для заполнения таблицы.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                    <h3 className="text-base font-bold text-foreground-800 mb-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-500 text-background-50 text-xs font-bold mr-2">2</span>
                      Заполните таблицу YOYBUY Spreadsheet
                    </h3>
                    <p className="text-sm text-foreground-600 ml-9">
                      Внесите в таблицу ссылки на товары, укажите нужные размеры, цвета, количество и другие параметры. Формат таблицы интуитивно понятен — каждая колонка соответствует определённому параметру заказа. Вы можете добавить сколько угодно строк для разных товаров.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                    <h3 className="text-base font-bold text-foreground-800 mb-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-500 text-background-50 text-xs font-bold mr-2">3</span>
                      Отправьте таблицу на обработку
                    </h3>
                    <p className="text-sm text-foreground-600 ml-9">
                      После заполнения всех данных отправьте таблицу через платформу YOYBUY. Система автоматически проверит корректность ссылок и параметров, рассчитает итоговую стоимость с учётом комиссий и доставки.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                    <h3 className="text-base font-bold text-foreground-800 mb-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-500 text-background-50 text-xs font-bold mr-2">4</span>
                      Оплатите и отслеживайте заказ
                    </h3>
                    <p className="text-sm text-foreground-600 ml-9">
                      Оплатите заказ удобным способом. После этого вы сможете отслеживать статус каждой позиции — от закупки у продавца до прибытия на склад и отправки международной доставкой. Все обновления статусов отображаются в вашем личном кабинете.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                    <h3 className="text-base font-bold text-foreground-800 mb-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-500 text-background-50 text-xs font-bold mr-2">5</span>
                      Получите товары
                    </h3>
                    <p className="text-sm text-foreground-600 ml-9">
                      Когда все позиции будут собраны на складе, вы сможете выбрать способ международной доставки и получить свои товары. YOYBUY предлагает несколько вариантов доставки с разными сроками и стоимостью.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Сравнение: YOYBUY Spreadsheet и другие способы заказа
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 px-3 text-xs font-semibold text-foreground-400 uppercase">Критерий</th>
                        <th className="text-center py-3 px-3 text-xs font-semibold text-primary-600 uppercase">YOYBUY Spreadsheet</th>
                        <th className="text-center py-3 px-3 text-xs font-semibold text-foreground-400 uppercase">Ручной заказ</th>
                        <th className="text-center py-3 px-3 text-xs font-semibold text-foreground-400 uppercase">Другие агенты</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-3 font-medium text-foreground-700">Время на 1 заказ</td>
                        <td className="text-center py-3 px-3 text-primary-600 font-semibold">2-3 мин</td>
                        <td className="text-center py-3 px-3 text-foreground-500">15-20 мин</td>
                        <td className="text-center py-3 px-3 text-foreground-500">5-10 мин</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-3 font-medium text-foreground-700">Вероятность ошибок</td>
                        <td className="text-center py-3 px-3 text-primary-600 font-semibold">Минимальная</td>
                        <td className="text-center py-3 px-3 text-foreground-500">Высокая</td>
                        <td className="text-center py-3 px-3 text-foreground-500">Средняя</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-3 font-medium text-foreground-700">Массовые заказы</td>
                        <td className="text-center py-3 px-3 text-primary-600 font-semibold">Оптимизировано</td>
                        <td className="text-center py-3 px-3 text-foreground-500">Крайне неудобно</td>
                        <td className="text-center py-3 px-3 text-foreground-500">Ограничено</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-3 font-medium text-foreground-700">Прозрачность комиссий</td>
                        <td className="text-center py-3 px-3 text-primary-600 font-semibold">Полная</td>
                        <td className="text-center py-3 px-3 text-foreground-500">Частичная</td>
                        <td className="text-center py-3 px-3 text-foreground-500">Средняя</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  Часто задаваемые вопросы о YOYBUY Spreadsheet
                </h2>

                <div className="space-y-4">
                  <div className="bg-background-100 rounded-2xl p-5">
                    <h3 className="text-sm font-bold text-foreground-800 mb-1">С каких платформ можно заказывать через YOYBUY Spreadsheet?</h3>
                    <p className="text-sm text-foreground-600">
                      Вы можете заказывать товары с Taobao, Tmall, 1688, Weidian, JD.com, Pinduoduo и большинства других китайских маркетплейсов. Таблица поддерживает ссылки со всех основных платформ.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-5">
                    <h3 className="text-sm font-bold text-foreground-800 mb-1">Сколько времени занимает обработка заказа?</h3>
                    <p className="text-sm text-foreground-600">
                      После отправки таблицы заказ обычно обрабатывается в течение нескольких часов. Закупка товаров у продавцов занимает 1-3 дня, после чего товары отправляются на склад YOYBUY для консолидации и международной доставки.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-5">
                    <h3 className="text-sm font-bold text-foreground-800 mb-1">Какие комиссии взимает YOYBUY?</h3>
                    <p className="text-sm text-foreground-600">
                      YOYBUY взимает прозрачную комиссию за обработку каждого заказа. Точный размер комиссии зависит от категории товара и отображается в таблице до отправки заказа. Дополнительно оплачивается только международная доставка, стоимость которой рассчитывается исходя из веса и габаритов посылки.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-2xl p-5">
                    <h3 className="text-sm font-bold text-foreground-800 mb-1">Можно ли вернуть товар, если он не подошёл?</h3>
                    <p className="text-sm text-foreground-600">
                      Да, возврат возможен в соответствии с политикой возврата конкретного продавца на китайской платформе. YOYBUY помогает организовать процесс возврата и обмена, минимизируя неудобства для покупателя.
                    </p>
                  </div>
                </div>
              </section>

              <section className="bg-background-100 rounded-2xl p-6 md:p-8 text-center">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                  Готовы попробовать YOYBUY Spreadsheet?
                </h2>
                <p className="text-sm md:text-base text-foreground-500 mb-6 max-w-md mx-auto leading-relaxed">
                  Начните заказывать товары из Китая проще, быстрее и без ошибок уже сегодня.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap no-underline"
                  >
                    Перейти к YOYBUY
                    <i className="ri-arrow-right-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <a
                    href="/"
                    className="inline-flex items-center gap-2 px-7 py-3.5 text-base font-semibold text-foreground-700 border border-background-300 hover:border-primary-300 hover:text-primary-500 rounded-full transition-colors whitespace-nowrap no-underline"
                  >
                    На главную
                    <i className="ri-home-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  </a>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}